import { TForgotPasswordRequestPayload, authService, forgotPasswordRequestSchema } from "@/api/auth";
import { FormEvent, useCallback } from "react";
import { DefaultValues, useForm } from "react-hook-form";

import { useToast } from "@/components/ui";
import { handleFormError } from "@/utils/formErrorUtils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";

export type TUseForgotPasswordFormProps = {
  defaultValues?: DefaultValues<TForgotPasswordRequestPayload>;
};

export const useForgotPasswordForm = ({ defaultValues }: TUseForgotPasswordFormProps = {}) => {
  const form = useForm<TForgotPasswordRequestPayload>({
    resolver: zodResolver(forgotPasswordRequestSchema),
    defaultValues,
  });

  const { toast } = useToast();

  const navigate = useNavigate();

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      form
        .handleSubmit(async (payload) => {
          const { data, error } = await authService.forgotPassword(payload);

          if (data) {
            toast({
              variant: "success",
              title: "Password reset email sent!",
              description: "Please check your inbox for instructions.",
            });
            void navigate({ to: "/auth/login", search: { from: undefined } });
          }

          if (error) {
            // Use shared form error handling
            await handleFormError(error, form);
          }
        })(e)
        .catch(console.error);
    },
    [form, toast, navigate]
  );

  return {
    form,
    handleSubmit,
  };
};
